import { appConfig } from 'brand-config';
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import {
  nonRatedIndividualReasonOptions,
  resolveNonRatedIndividualReasonLabel,
  withCurrentReasonOption
} from './reason-options';
import { useFormikContext } from 'formik';
import { format, parseISO } from 'date-fns';
import { useParams } from 'react-router-dom';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import { track } from 'core/helpers/analytics';
import { useToast } from 'core/components/toast';
import { capitalize } from 'core/helpers/formatters';
import useSession from 'core/hooks/use-session';
import RemoveButton from 'core/components/remove-button';
import RemoveLockedButton from 'core/components/remove-locked-button';
import { Label } from 'core/components/label';
import CollapsibleCard from 'core/components/collapsible-card';
import { FormField } from 'core/components/form';
import ConfirmRemoveNonRatedIndividualModal from './confirm-remove-modal';
import useStyles from './non-rated-individuals.styles';
import Toast from 'core/components/toast/toast';

const Individual = ({ item, fieldName, disabled, initialOpen = false }) => {
  const classes = useStyles();
  const session = useSession();
  const { canModifyNonRatedIndividual } = session;
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const [openConfirmRemoveModal, setOpenConfirmRemoveModal] = useState(false);
  const toast = useToast();
  const { inPolicyRoute, inOfferEditRoute } = useRouteHelper();
  const params = useParams();

  const handleRemove = useCallback(async () => {
    setFieldValue(
      'removedDrivers',
      values.removedDrivers.filter((driver) => driver.id !== item.id)
    );
    setFieldTouched('removedDrivers');
    toast.notify({
      type: 'success',
      message: `${capitalize(item.firstName)} ${capitalize(item.lastName)} was successfully removed as a non-rated individual.`
    });

    await track('Non-rated individual removed', {
      id: inPolicyRoute ? params.policyId : params.offerId,
      personRemoved: { ...item },
      agent: session
    });
  }, [item, setFieldValue, setFieldTouched, values, toast, session, params, inPolicyRoute]);

  const undoAddPersonAsDriver = () => {
    setFieldValue(
      'drivers',
      values.drivers.filter((driver) => driver.id !== item.id)
    );
    setFieldTouched('drivers');

    setFieldValue('removedDrivers', [
      ...values.removedDrivers.filter((driver) => driver.id !== item.id),
      {
        ...item,
        removedBy: session.user.username,
        removedTimestamp: new Date(),
        removedReason: null
      }
    ]);
    setFieldTouched('removedDrivers');
  };

  const handleAddPersonAsDriver = () => {
    setFieldValue('drivers', [
      ...values.drivers,
      {
        ...item,
        removedReason: null,
        removedBy: null,
        removedTimestamp: null,
        autoViolations: {},
        autoViolationsForUW: {}
      }
    ]);
    setFieldTouched('drivers');

    setFieldValue(
      'removedDrivers',
      values.removedDrivers.filter((driver) => driver.id !== item.id)
    );

    setFieldTouched('removedDrivers');
    toast.notify({
      type: 'success',
      message: `${capitalize(item.firstName)} ${capitalize(item.lastName)} was successfully added as a driver and removed as a non-rated individual.`,
      label: 'Undo',
      action: undoAddPersonAsDriver
    });

    track('Non-rated individual added as driver', {
      id: inPolicyRoute ? params.policyId : params.offerId,
      personAdded: item,
      agent: session
    });
  };

  const reasonOptions = withCurrentReasonOption(
    nonRatedIndividualReasonOptions({
      restrictToCfm: appConfig?.featureFlags?.restrictNonRatedIndividualReasons,
      extra: 'all'
    }),
    item.removedReason
  );
  return (
    <>
      <ConfirmRemoveNonRatedIndividualModal
        onClose={() => setOpenConfirmRemoveModal(false)}
        onConfirm={handleRemove}
        open={openConfirmRemoveModal}
        name={`${capitalize(item.firstName)} ${capitalize(item.lastName)}`}
      />
      <CollapsibleCard
        initialOpen={initialOpen}
        content={
          <Label type="infoSubtitle">
            {item.firstName} {item.lastName}
          </Label>
        }
        contentRight={
          canModifyNonRatedIndividual ? (
            <RemoveButton mode="big" onClick={() => setOpenConfirmRemoveModal(true)} />
          ) : (
            <RemoveLockedButton
              mode="big"
              text="You do not have permissions to edit a non-rated individual. If you need to remove or edit them, please contact support."
              disabled={disabled}
            />
          )
        }
        disabled={disabled}
      >
        <Grid container className={classes.innerCardcontainer}>
          <Grid item xs={4}>
            <FormField
              id={`${fieldName}.dateOfBirth`}
              name={`${fieldName}.dateOfBirth`}
              type="value"
              label="Date of birth"
              mode="light"
              value={item.dateOfBirth ? format(parseISO(item.dateOfBirth), 'MM/dd/yyyy') : 'N/A'}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField
              id={`${fieldName}.driversLicenseState`}
              name={`${fieldName}.driversLicenseState`}
              type="value"
              label="License/ID State"
              mode="light"
              value={item.driversLicenseState || 'Unknown'}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField
              id={`${fieldName}.driversLicenseNumber`}
              name={`${fieldName}.driversLicenseNumber`}
              type="value"
              label="License/ID Number"
              mode="light"
              value={item.driversLicenseNumber ?? 'Unknown'}
            />
          </Grid>

          <Grid item xs={12}>
            {canModifyNonRatedIndividual || inOfferEditRoute ? (
              <FormField
                id={`${fieldName}.removedReason`}
                name={`${fieldName}.removedReason`}
                type="select"
                label="Reason for being a non-rated individual"
                mode="light"
                options={reasonOptions?.map((r) => {
                  return { ...r, disabled: r.disabled || r.automationOnly || (r.policyOnly && inOfferEditRoute) };
                })}
                onChange={() => {
                  setFieldValue(`${fieldName}.removedBy`, session.user.username);
                  setFieldValue(`${fieldName}.removedTimestamp`, new Date());
                }}
              />
            ) : (
              <FormField
                id={`${fieldName}.removedReason`}
                name={`${fieldName}.removedReason`}
                type="value"
                label="Reason for being a non-rated individual"
                mode="light"
                value={resolveNonRatedIndividualReasonLabel(item.removedReason)}
              />
            )}
          </Grid>
          <div className={classes.driverHint}>
            <span className={classes.infoIcon}>ⓘ</span>
            <div className={classes.noteText}>
              Should this person be a driver on the policy?{' '}
              <span className={classes.addDriverLink} onClick={handleAddPersonAsDriver}>
                Add them as a driver
              </span>{' '}
              instead.
            </div>
          </div>
        </Grid>
      </CollapsibleCard>
      <Toast />
    </>
  );
};

Individual.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    dateOfBirth: PropTypes.string.isRequired,
    removedReason: PropTypes.string,
    driversLicenseNumber: PropTypes.string,
    driversLicenseState: PropTypes.string
  }).isRequired,
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

Individual.defaultProps = {
  disabled: false
};

export default Individual;
